import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  isValidEmail,
  isValidPassword,
  isValidUsername,
  isValidActivationCode,
  formatActivationCode,
} from '../utils/validation'

export default function Register() {
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  // 邀请链接 ?code=XXXX 自动填入卡密
  const [activationCode, setActivationCode] = useState(searchParams.get('code') || '')
  // 注册身份：user 普通用户 / merchant 商家入驻
  const [role, setRole] = useState(searchParams.get('role') === 'merchant' ? 'merchant' : 'user')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { register } = useAuth()
  const navigate = useNavigate()

  // 即时校验：全部通过时按钮高亮可用
  const emailOk = isValidEmail(email)
  const usernameOk = isValidUsername(username)
  const pwdOk = isValidPassword(password)
  const confirmOk = confirm.length > 0 && confirm === password
  const codeOk = !activationCode.trim() || isValidActivationCode(activationCode)
  const canSubmit = emailOk && usernameOk && pwdOk && confirmOk && codeOk && !submitting

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (!canSubmit) return
    setSubmitting(true)
    try {
      await register(email, password, username.trim(), activationCode.trim(), role)
      navigate('/')
    } catch (err) {
      setError(err.response?.data?.error || err.message || '注册失败')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto mt-10 flex max-w-md flex-col items-center">
      <div className="w-full rounded-[2rem] border border-blush/30 bg-white p-8 shadow-card">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-mint-light text-3xl">
            {role === 'merchant' ? '🏪' : '🌱'}
          </div>
          <h1 className="text-2xl font-bold text-cocoa">
            {role === 'merchant' ? '商家入驻' : '加入豆田造物'}
          </h1>
          <p className="mt-1 text-sm text-cocoa-light">
            {role === 'merchant' ? '开通商家账号，面向拼豆爱好者展示你的店铺~' : '开启你的甜甜拼豆创作之旅~'}
          </p>
        </div>

        {/* 身份选择：普通用户 / 商家入驻 */}
        <div className="mb-4 grid grid-cols-2 gap-2">
          {[
            { value: 'user', label: '用户注册', icon: '🌱' },
            { value: 'merchant', label: '商家入驻', icon: '🏪' },
          ].map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRole(r.value)}
              className={`rounded-2xl border py-2.5 text-sm font-semibold transition ${
                role === r.value
                  ? 'border-blush bg-blush text-white shadow-soft'
                  : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
              }`}
            >
              {r.icon} {r.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-cocoa">邮箱</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={`form-input ${email && !emailOk ? 'border-red-300' : ''}`}
              placeholder="you@example.com"
            />
            {email && !emailOk && <p className="mt-1 text-xs text-red-500">邮箱格式不正确</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-cocoa">用户名</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={2}
              maxLength={20}
              className={`form-input ${username && !usernameOk ? 'border-red-300' : ''}`}
              placeholder="豆田小能手"
            />
            <p className={`mt-1 text-xs ${username && !usernameOk ? 'text-red-500' : 'text-cocoa-light'}`}>
              {username && !usernameOk ? '用户名需 2-20 个字符' : '2-20 个字符，支持中文、字母、数字、空格、下划线、连字符'}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-cocoa">密码</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className={`form-input ${password && !pwdOk ? 'border-red-300' : ''}`}
              placeholder="••••••"
            />
            {password && !pwdOk && <p className="mt-1 text-xs text-red-500">密码至少 6 位</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-cocoa">确认密码</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              className={`form-input ${confirm && !confirmOk ? 'border-red-300' : ''}`}
              placeholder="••••••"
            />
            {confirm && !confirmOk && <p className="mt-1 text-xs text-red-500">两次输入的密码不一致</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-cocoa">
              卡密激活码 <span className="text-xs font-normal text-cocoa-light">（选填）</span>
            </label>
            <input
              type="text"
              value={activationCode}
              onChange={(e) => setActivationCode(formatActivationCode(e.target.value))}
              className={`form-input ${activationCode && !codeOk ? 'border-red-300' : ''}`}
              placeholder="XXXX-XXXX-XXXX-XXXX"
            />
            {activationCode && !codeOk ? (
              <p className="mt-1 text-xs text-red-500">卡密格式为 XXXX-XXXX-XXXX-XXXX</p>
            ) : (
              <p className="mt-1 text-xs text-cocoa-light">
                选填：会员卡 / AI 额度卡均可，注册后立即生效；也可以先注册，之后在「个人中心 → 兑换卡密」中兑换
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={!canSubmit}
            className={`btn-primary w-full transition ${
              canSubmit ? 'shadow-md' : 'cursor-not-allowed opacity-40'
            }`}
          >
            {submitting ? '注册中…' : '注册'}
          </button>
        </form>

        <p className="mt-5 text-center text-sm text-cocoa-light">
          已有账号？{' '}
          <Link to="/login" className="font-semibold text-blush-dark hover:underline">
            直接登录
          </Link>
        </p>
      </div>
    </div>
  )
}
